import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { createEffect } from '@ngrx/effects';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import * as assetsSapActions from 'app/actions/sap-assets';
import { RouteStore } from 'app/state-management/route/route.store';
import { UrlUtils } from '../utils/url-utils';
import { assetsActions } from '../actions/assets';

/**
 * Keeps the NgRx-driven asset selection in step with navigation: it has to survive leaving the tree.
 * The route itself lives in `RouteStore`.
 */
@Injectable()
export class RouteSyncEffects {
  private router: Router = inject(Router);
  private routeStore = inject(RouteStore);

  // the route store moves on at RoutesRecognized and leaves previousUrl alone at NavigationEnd,
  // so the reads below see the finished navigation and the url it started from
  private navigationEnded$: Observable<NavigationEnd> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
  );

  navigationEndCheckForStoringRegularAssetSelection$ = createEffect(() => this.navigationEnded$.pipe(
    filter(() => this.checkWhetherWeNeedToStoreAssetSelected(
      this.routeStore.url(),
      this.routeStore.isRegularAssetZoneActivated(),
      this.routeStore.isRegularAssetZoneActivatedForPreviousUrl(),
    )),
    map(() => assetsActions.storeSelectedRegularNode({
      selected: { assetId: this.routeStore.assetId(), nodeId: this.routeStore.assetNodeId() },
    }))
  ));

  navigationEndCheckForStoringSapAssetSelection$ = createEffect(() => this.navigationEnded$.pipe(
    filter(() => this.checkWhetherWeNeedToStoreAssetSelected(
      this.routeStore.url(),
      this.routeStore.isSapAssetZoneActivated(),
      this.routeStore.isSapAssetZoneActivatedForPreviousUrl(),
    )),
    map(() => assetsSapActions.storeSelectedSapNode({ selected: this.routeStore.selectedSapAsset() }))
  ));

  private checkWhetherWeNeedToStoreAssetSelected(
    url: string,
    isAssetZone: boolean,
    isAssetZoneForPreviousUrl: boolean,
  ): boolean {
    const primaryUrl: string = UrlUtils.getPrimaryRouterUrl(url);

    return primaryUrl?.includes('machines') && isAssetZone && isAssetZoneForPreviousUrl;
  }
}
